/**
 * Currency and language switcher.
 * - "url:<store>"  moves the visitor to that currency's own store at the same path and query;
 *                  the cookie tells the stores' geo redirect not to send them back.
 * - "cc:<country>" and "lang:<code>" post Shopify's localization form, so prices, taxes,
 *                  shipping and the /de market follow.
 */
class FaStoreSwitcher extends HTMLElement {
  connectedCallback() {
    this.selects = [...this.querySelectorAll('select')];
    for (const select of this.selects) select.addEventListener('change', this.#onChange);
  }

  disconnectedCallback() {
    for (const select of this.selects || []) select.removeEventListener('change', this.#onChange);
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const select = /** @type {HTMLSelectElement} */ (event.currentTarget);
    const value = select.value || '';
    if (value.startsWith('url:')) return this.#switchStore(value.slice(4));
    if (value.startsWith('cc:')) return this.#localize({ country_code: value.slice(3) });
    if (value.startsWith('lang:')) return this.#localize({ language_code: value.slice(5) });
  };

  /** @param {string} base */
  #switchStore(base) {
    let target;
    try {
      target = new URL(window.location.pathname + window.location.search, base);
    } catch {
      return;
    }
    // A preview of an unpublished theme only exists on this store
    target.searchParams.delete('preview_theme_id');
    // The other stores have no /de pages
    target.pathname = target.pathname.replace(/^\/[a-z]{2}(?=\/|$)/, '');
    document.cookie = 'currency_override=1; path=/; domain=.farandaway.co; max-age=86400';
    window.location.assign(target.toString());
  }

  /** @param {Record<string, string>} fields */
  #localize(fields) {
    const form = this.querySelector('form');
    if (!form) return;
    for (const [name, value] of Object.entries(fields)) {
      const input = form.querySelector(`input[name="${name}"]`);
      if (input) input.value = value;
    }
    // The header is re-rendered through the section API, whose copy of this form carries that
    // request's URL as return_to. Always come back to the page the shopper is looking at.
    const returnTo = form.querySelector('input[name="return_to"]');
    if (returnTo) {
      const here = new URL(window.location.href);
      here.searchParams.delete('section_id');
      here.searchParams.delete('sections');
      returnTo.value = here.pathname + here.search;
    }
    // Leaving a currency that was chosen by hand must not be undone by the geo redirect
    document.cookie = 'currency_override=1; path=/; domain=.farandaway.co; max-age=86400';
    form.submit();
  }
}

if (!customElements.get('fa-store-switcher')) {
  customElements.define('fa-store-switcher', FaStoreSwitcher);
}
